import { styled } from "styled-components";
import { VictoryLine } from "victory";
import { lazy, memo, Suspense, useMemo } from "react";
import useMeasure from "react-use-measure";
import {
  Spinner,
  Text,
  theme,
  useParsedSearchParams,
} from "@fidelix/fx-miranda";
import { Trans } from "@lingui/react/macro";
import { isEqual } from "lodash-es";
import {
  HistoryReplayContextProps,
  useHistoryReplayContext,
} from "./history-replay-context";
import { PointHistoryItem } from "../../point/point.model";

export type HistoryItem = Pick<PointHistoryItem, "id" | "value" | "timestamp">;

type Props = { isResizing: boolean; width: number };
export function HistoryReplayZoom({ isResizing, width }: Props) {
  const {
    isLoading,
    onZoomDomainChange,
    zoomDomain,
    previewData,
    previewDataIsFetching,
    brushPosition,
    fromInSeconds,
    toInSeconds,
    playbackSpeed,
  } = useHistoryReplayContext();

  return (
    <HistoryReplayZoomComponent
      playbackSpeed={playbackSpeed}
      fromInSeconds={fromInSeconds}
      toInSeconds={toInSeconds}
      brushPosition={brushPosition}
      isResizing={isResizing}
      isLoading={isLoading}
      onZoomDomainChange={onZoomDomainChange}
      zoomDomain={zoomDomain}
      previewData={previewData}
      previewDataIsFetching={previewDataIsFetching}
      width={width}
    />
  );
}

type ZoomProps = Pick<
  HistoryReplayContextProps,
  | "isLoading"
  | "onZoomDomainChange"
  | "previewData"
  | "previewDataIsFetching"
  | "brushPosition"
  | "fromInSeconds"
  | "toInSeconds"
  | "playbackSpeed"
  | "zoomDomain"
> &
  Props;
function Component({
  isLoading,
  onZoomDomainChange,
  previewData,
  previewDataIsFetching,
  isResizing,
  brushPosition,
  fromInSeconds,
  toInSeconds,
  width,
  zoomDomain,
}: ZoomProps) {
  const [containerRef, { height }] = useMeasure();
  // Lazy load the chart component
  // This is done for performance reasons. With big datasets the chart
  // might take 1-2 seconds to actually redraw and thus make the UI unresponsive
  // Only "re-lazyload" it when then container height is changed - as in when the
  // user resizes the replay container size
  const Chart = useMemo(
    () => lazy(() => import("./history-replay-chart")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [height],
  );
  const { parsedParams } = useParsedSearchParams({
    to: { type: "string", defaultValue: undefined },
    from: { type: "string", defaultValue: undefined },
  });

  const data: HistoryItem[][] = useMemo(
    () =>
      isLoading
        ? []
        : Object.values(previewData ?? {})
            ?.filter((v) => v != undefined)
            .map((o) => {
              return o.map((v, i) => ({
                id: i,
                value: v.value == undefined || isNaN(v.value) ? null : v.value,
                timestamp: v.timestampJS,
              }));
            }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [Object.keys(previewData || {}).length, zoomDomain?.y],
  );

  const renderedLines = useMemo(() => getDataLines(data), [data]);

  return (
    <Container ref={containerRef}>
      {previewDataIsFetching ? (
        <EmptyContainer>
          <Spinner size="medium" />
        </EmptyContainer>
      ) : isResizing ? (
        <EmptyContainer>
          <Text variant="subtitle1">
            <Trans>Release mouse to redraw</Trans>
          </Text>
        </EmptyContainer>
      ) : (
        // Show a spinner instead of the chart when it is loading
        <Suspense
          fallback={
            <EmptyContainer>
              <Spinner size="medium" />
            </EmptyContainer>
          }
        >
          <Chart
            fromInSeconds={fromInSeconds}
            toInSeconds={toInSeconds}
            brushPosition={brushPosition}
            height={height - theme.spaceNumber("xsmall")}
            onZoomDomainChange={onZoomDomainChange}
            zoomDomain={zoomDomain}
            prevFrom={parsedParams.from}
            prevTo={parsedParams.to}
            width={width}
            renderedLines={renderedLines}
          />
        </Suspense>
      )}
    </Container>
  );
}

export function getDataLines(historyData?: HistoryItem[][]) {
  if (!historyData || historyData.length === 0) {
    return;
  }

  return historyData.map((d, i) => (
    <VictoryLine
      key={`graphics-history-key ${i}`}
      name={`graphics-history-name ${i}`}
      data={d.map((item) => ({
        ...item,
        timestamp: item.timestamp.toJSDate(),
      }))}
      x="timestamp"
      y="value"
      style={{
        data: {
          pointerEvents: "none",
          color: "#000",
        },
      }}
    />
  ));
}

// NOTE: Unlike normally we are using memoizing here for the reason
// that this component is expensive to render, and would hinder performance
// by a substantial amount if it renders every frame when the playhead is moving
const HistoryReplayZoomComponent = memo(Component, arePropsEqual);
function arePropsEqual(oldProps: ZoomProps, newProps: ZoomProps) {
  if (!isEqual(oldProps.previewData, newProps.previewData)) {
    return false;
  }
  if (oldProps.zoomDomain !== newProps.zoomDomain) {
    return false;
  }
  if (oldProps.width !== newProps.width) {
    return false;
  }

  if (oldProps.previewDataIsFetching !== newProps.previewDataIsFetching) {
    return false;
  }
  if (oldProps.playbackSpeed !== newProps.playbackSpeed) {
    return false;
  }
  if (newProps.isResizing || oldProps.isResizing !== newProps.isResizing) {
    return false;
  }
  if (oldProps.previewDataIsFetching || newProps.previewDataIsFetching) {
    return false;
  }
  return true;
}

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

const EmptyContainer = styled.div`
  width: calc(100% - ${(p) => p.theme.space("small")});
  margin-left: ${(p) => p.theme.space("xxsmall")};
  height: 100%;
  border: 2px solid ${(p) => p.theme.colors.black}28;
  display: flex;
  justify-content: center;
  align-items: center;
`;
